import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { ip_back } from "../config";
import { DEFAULT_BRAND, applyCountryLangBrandDefaults } from "../constants/brandDefaults";
import { LAST_BRAND_CLUB_ID_KEY } from "../utils/localStorageBrand";
import { getClubIdFromInvitationSearch } from "../utils/inviteClubId";
import { sanitizeBrandLogoUrl } from "../utils/resolveClubImageUrl";
import { applyPlatformBrand, isSuperAdminUser, resolveClubIdForBrand, applyFullBrandTheme } from "../utils/brandAuth";
import { clubLanguageFromStoredUser } from "../utils/clubCountryLanguage";

const BrandContext = createContext({
  brand: DEFAULT_BRAND,
  loading: true,
  themeMode: "light",
  setThemeMode: () => {},
  applyBrandPreview: () => {},
  clearBrandPreview: () => {},
});

function getSignedInUser() {
  try {
    return JSON.parse(localStorage.getItem("user") || "null");
  } catch {
    return null;
  }
}

function cachedBrandForUser(parsed, user) {
  if (!parsed || typeof parsed !== "object") return null;
  if (isSuperAdminUser(user)) return null;
  if (user?.user_id) {
    const userClub = resolveClubIdForBrand(user);
    const prevClub = parsed.club_id != null ? String(parsed.club_id) : "";
    if (userClub && prevClub && prevClub !== userClub) {
      return null;
    }
  }
  return parsed;
}

/** Club id for /api/brand/resolve: signed-in user's club, else invite / last session. */
function getClubIdForBrandResolve() {
  try {
    const user = getSignedInUser();
    if (isSuperAdminUser(user)) {
      return null;
    }
    if (user?.user_id) {
      return resolveClubIdForBrand(user);
    }
  } catch {
    /* ignore */
  }
  const fromInvite = getClubIdFromInvitationSearch();
  if (fromInvite) return fromInvite;
  try {
    const last = localStorage.getItem(LAST_BRAND_CLUB_ID_KEY);
    if (last && String(last).trim()) return String(last).trim();
  } catch {
    /* ignore */
  }
  try {
    const grp = JSON.parse(localStorage.getItem("grpinf") || "null");
    const cid = grp?.groupDetails?.club_id;
    if (cid) return String(cid).trim();
  } catch {
    /* ignore */
  }
  return null;
}

function getClubCodeFromJoinPath() {
  try {
    const path = window.location.pathname || "";
    const match = path.match(/\/join\/([^/?#]+)/i);
    return match ? decodeURIComponent(match[1]) : null;
  } catch {
    return null;
  }
}

function getClubCodeFromDemoPath() {
  try {
    const path = String(window.location.pathname || "");
    if (/(?:^|\/)demo\/?$/i.test(path) || /(?:^|\/)demo(?:\/|$)/i.test(path)) {
      return "demo";
    }
    return null;
  } catch {
    return null;
  }
}

function withCountryLangDefaults(brand, user = getSignedInUser()) {
  if (isSuperAdminUser(user)) return brand;
  return applyCountryLangBrandDefaults(brand, clubLanguageFromStoredUser(user));
}

export function BrandProvider({ children }) {
  const brandPreviewLockRef = React.useRef(false);

  const [brand, setBrand] = useState(() => {
    try {
      if (isSuperAdminUser(getSignedInUser())) {
        const platform = applyPlatformBrand();
        return platform;
      }
      const cached = localStorage.getItem("brand_config");
      if (cached) {
        const parsed = cachedBrandForUser(
          withCountryLangDefaults(sanitizeBrandLogoUrl(JSON.parse(cached))),
          getSignedInUser()
        );
        if (parsed) {
          applyFullBrandTheme(parsed);
          return parsed;
        }
      }
    } catch {}
    applyFullBrandTheme(DEFAULT_BRAND);
    return DEFAULT_BRAND;
  });
  const [loading, setLoading] = useState(true);

  const fetchBrand = useCallback(async () => {
    if (brandPreviewLockRef.current) {
      setLoading(false);
      return;
    }
    try {
      if (isSuperAdminUser(getSignedInUser())) {
        if (brandPreviewLockRef.current) {
          setLoading(false);
          return;
        }
        const platform = applyPlatformBrand();
        if (brandPreviewLockRef.current) {
          setLoading(false);
          return;
        }
        setBrand(platform);
        setLoading(false);
        return;
      }

      const clubId = getClubIdForBrandResolve();
      const clubCode = getClubCodeFromJoinPath() || getClubCodeFromDemoPath();
      const hostname = window.location.hostname;
      const signedIn = Boolean(getSignedInUser()?.user_id) && !isSuperAdminUser(getSignedInUser());

      const params = new URLSearchParams();
      // Signed-in users must resolve by their club, not the host (shared app URL or another club's subdomain).
      if (!signedIn && hostname && hostname !== "localhost") params.set("hostname", hostname);
      if (clubId) params.set("club_id", clubId);
      if (clubCode) params.set("club_code", clubCode);

      const response = await fetch(`${ip_back}api/brand/resolve?${params.toString()}`);
      if (!response.ok) throw new Error("Brand resolve failed");

      const result = await response.json();
      if (result.status === "success" && result.data) {
        const isDefault = result.is_default === true;
        const newBrand = withCountryLangDefaults(
          sanitizeBrandLogoUrl({ ...DEFAULT_BRAND, ...result.data })
        );

        // Server returned platform defaults but we asked for a specific club — keep cached look
        // only when the cache is that same club (never another club's leftover theme).
        if (isDefault && clubId) {
          try {
            const raw = localStorage.getItem("brand_config");
            if (raw) {
              const prev = sanitizeBrandLogoUrl(JSON.parse(raw));
              const prevClub = prev.club_id != null ? String(prev.club_id) : null;
              if (prevClub && prevClub === clubId) {
                setBrand(prev);
                applyFullBrandTheme(prev, { persist: true });
                setLoading(false);
                return;
              }
            }
          } catch {
            /* fall through to apply server payload */
          }
        }

        // Logged out, no club hint — keep last club theme on landing/login.
        if (isDefault && !clubId && !getSignedInUser()?.user_id) {
          try {
            const raw = localStorage.getItem("brand_config");
            if (raw) {
              const prev = sanitizeBrandLogoUrl(JSON.parse(raw));
              const looksCustom =
                (prev.logo_url && prev.logo_url !== DEFAULT_BRAND.logo_url) ||
                (prev.color_primary && prev.color_primary !== DEFAULT_BRAND.color_primary) ||
                (prev.brand_name && prev.brand_name !== DEFAULT_BRAND.brand_name);
              if (looksCustom) {
                setBrand(prev);
                applyFullBrandTheme(prev, { persist: true });
                setLoading(false);
                return;
              }
            }
          } catch {
            /* apply default */
          }
        }

        setBrand(newBrand);
        applyFullBrandTheme(newBrand, { persist: true });
        localStorage.setItem("brand_config", JSON.stringify(newBrand));

        if (clubId) {
          localStorage.setItem(LAST_BRAND_CLUB_ID_KEY, clubId);
        }
        if (!isDefault && newBrand.club_id != null) {
          localStorage.setItem(LAST_BRAND_CLUB_ID_KEY, String(newBrand.club_id));
        }
      }
    } catch (err) {
      console.warn("Brand resolution failed, using defaults:", err.message);
      // Do not call injectCssVariables with a stale closure; keep current DOM/theme.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    try {
      const raw = localStorage.getItem("brand_config");
      if (raw && !localStorage.getItem(LAST_BRAND_CLUB_ID_KEY)) {
        const b = JSON.parse(raw);
        if (b?.club_id) {
          localStorage.setItem(LAST_BRAND_CLUB_ID_KEY, String(b.club_id));
        }
      }
    } catch {
      /* ignore */
    }
    applyFullBrandTheme(brand);
    fetchBrand();
  }, [fetchBrand]);

  const applyBrandPreview = useCallback((nextBrand) => {
    const merged = applyFullBrandTheme(
      sanitizeBrandLogoUrl({ ...DEFAULT_BRAND, ...(nextBrand || {}) }),
      { persist: true }
    );
    brandPreviewLockRef.current = true;
    setBrand(merged);
    return merged;
  }, []);

  const clearBrandPreview = useCallback(async () => {
    brandPreviewLockRef.current = false;
    await fetchBrand();
  }, [fetchBrand]);

  // Other tabs: re-fetch when auth changes. Same-tab login also dispatches smarttt:auth-changed.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === "user" || e.key === "token" || e.key === "grpinf") {
        brandPreviewLockRef.current = false;
        fetchBrand();
      }
    };
    const onAuthChanged = () => {
      brandPreviewLockRef.current = false;
      fetchBrand();
    };
    const onClubChanged = () => {
      brandPreviewLockRef.current = false;
      fetchBrand();
    };
    window.addEventListener("storage", onStorage);
    window.addEventListener("smarttt:auth-changed", onAuthChanged);
    window.addEventListener("smarttt-club-changed", onClubChanged);
    window.addEventListener("smarttt-user-updated", onClubChanged);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("smarttt:auth-changed", onAuthChanged);
      window.removeEventListener("smarttt-club-changed", onClubChanged);
      window.removeEventListener("smarttt-user-updated", onClubChanged);
    };
  }, [fetchBrand]);

  const [themeMode, setThemeModeState] = useState(() => {
    return localStorage.getItem("app_theme_mode") || "light";
  });

  const setThemeMode = useCallback((mode) => {
    setThemeModeState(mode);
    localStorage.setItem("app_theme_mode", mode);
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    const isLight = themeMode === "light";
    root.setAttribute("data-theme", themeMode);
    root.classList.toggle("light-mode", isLight);
    root.classList.toggle("dark-mode", !isLight);
    document.body.classList.toggle("light-mode", isLight);
    document.body.classList.toggle("dark-mode", !isLight);
  }, [themeMode]);

  return (
    <BrandContext.Provider
      value={{
        brand,
        loading,
        refreshBrand: fetchBrand,
        applyBrandPreview,
        clearBrandPreview,
        themeMode,
        setThemeMode,
      }}
    >
      {children}
    </BrandContext.Provider>
  );
}

export function useBrand() {
  return useContext(BrandContext);
}

export { DEFAULT_BRAND } from "../constants/brandDefaults";
export default BrandContext;
